import React from "react";
import Select from "../../common/select";
import axios, { errorHandler, responseHandler } from "../../requests/axios";
import { toast } from "react-toastify";
import Loader from "../../common/loader";

function RepeatSchedulingFormMeta({ episode }) {
  function getGenre() {
    return episode.genre?.imdb?.imdb_genre?.label || null;
  }

  function getRating() {
    return episode.rating?.bbfc_rating || episode.rating?.mpaa_rating || episode.rating?.us_tv_rating || null;
  }

  function getYear() {
    const date = episode.published_at || episode.original_air_date || null;
    return date ? new Date(episode.published_at || episode.original_air_date).getFullYear() : null;
  }

  return (
    <div className="repeat-scheduling-form-meta">
      <div className="repeat-scheduling-form-meta__text-field">
        <div className="repeat-scheduling-form-meta__text-field__label">Episode Name:</div>
        <div className="repeat-scheduling-form-meta__text-field__value">{episode.episode_name}</div>
      </div>

      <div className="repeat-scheduling-form-meta__text-field repeat-scheduling-form-meta__text-field--vertical">
        <div className="repeat-scheduling-form-meta__text-field__label">Summary:</div>
        <div className="repeat-scheduling-form-meta__text-field__value">
          {episode.summary?.long_summary || episode.summary.short_summary || "No Summary"}
        </div>
      </div>

      <div className="repeat-scheduling-form-meta__text-field">
        {getGenre() ? <div className="repeat-scheduling-form-meta__text-field__label">{getGenre()}</div> : null}
        {getRating() ? <div className="repeat-scheduling-form-meta__text-field__label">{getRating()}</div> : null}
        {getYear() ? <div className="repeat-scheduling-form-meta__text-field__label">{getYear()}</div> : null}
      </div>
    </div>
  );
}

export default function RepeatSchedulingForm({ onClose, onSuccess, repeater, item, templateId }) {
  const defaultForm = {
    index: null,
    template_id: templateId,
    content_type: item.type,
    content_guid: item.id,
    excluded: [],
    repeat_type: "loop",
    order_type: "linear",
    start_point: "last",
    start_season: null,
    start_episode: null,
  };
  const [form, setForm] = React.useState(repeater ?? defaultForm);
  const [seasons, setSeasons] = React.useState([]);
  const [isSubmitting, setSubmitting] = React.useState(false);

  const orderTypeOptions = [
    { label: "In Order", value: "linear" },
    { label: "Shuffle", value: "shuffle" },
  ];

  const repeatTypeOptions = [
    { label: "Return to start", value: "loop" },
    { label: "Stop after last episode", value: "end" },
  ];

  const startingOptions = [
    { label: "Selection", value: "select" },
    { label: "From Last Scheduled", value: "last" },
  ];

  const updateForm = React.useCallback((label, value) => {
    setForm((prev) => ({
      ...prev,
      [label]: value,
    }));
  }, []);

  const validateForm = React.useCallback(() => {
    if (form.start_point === "select" && (!form.start_season || !form.start_episode)) {
      toast.info("For Starting Point Selection, a program must be selected. Otherwise, use From Last Scheduled");
      return false;
    }

    return true;
  }, [form.start_episode, form.start_point, form.start_season]);

  const submitForm = React.useCallback(() => {
    setSubmitting(true);

    if (!validateForm()) {
      setSubmitting(false);
      return;
    }

    const payload = {
      ...form,
      content_id: seasons[0].series_id, // TemplateDayItem only holds the guid
    };

    if (form.template_content_repeater_id) {
      payload.template_content_repeater_id = form.template_content_repeater_id;
    }

    payload.rules = [];
    if (form.start_point === "select" && form.start_episode) {
      const startRule = {
        rule_type: "starts_at",
        link_type: "episode",
        link_guid: form.start_episode.episode_guid,
        link_id: form.start_episode.episode_id,
      };
      payload.rules.push(startRule);
    }

    payload.selections = form.excluded.map((season) => ({
      selection_type: "exclude",
      content_type: "season",
      content_guid: season.season_guid,
      content_id: season.season_id,
    }));

    axios
      .put(`api/templates/${templateId}/repeaters`, payload)
      .then(
        responseHandler(() => {
          onSuccess(payload);
          toast.success("Successfully updated Template Content Repeater");
          onClose();
        }),
      )
      .catch(
        errorHandler((e) => {
          console.error(e);
          toast.error("Error: Could not update Template Content Repeater");
        }),
      )
      .finally(() => setSubmitting(false));
  }, [form, onClose, onSuccess, seasons, validateForm, templateId]);

  React.useEffect(() => {
    if (form.content_guid) {
      axios
        .get(`api/series/${form.content_guid}/seasons?episodes=true`)
        .then(
          responseHandler((resp) => {
            updateForm(
              "seasons",
              resp.data.map((season) => season.season_guid),
            );
            setSeasons(resp.data.sort((a, b) => (a.season_number < b.season_number ? -1 : 1)));
          }),
        )
        .catch(
          errorHandler((e) => {
            console.error(e);
            toast.error("Error loading seasons for this series");
          }),
        );
    }
  }, [updateForm, form.content_guid]);

  React.useEffect(() => {
    if (seasons.length && form.start_episode && !form.start_season) {
      const startSeason = seasons.find((season) => {
        return season.episodes.some((episode) => episode.episode_guid === form.start_episode.episode_guid);
      });

      updateForm("start_season", startSeason || null);
    }
  }, [form, seasons, updateForm]);

  function updateExcludedContent(checked, season) {
    let excludedContent = [...form.excluded];
    if (!checked) {
      excludedContent = [...excludedContent, { season_guid: season.season_guid, season_id: season.season_id }];
      if (form.start_season && season.season_guid === form.start_season.season_guid) {
        updateForm("start_episode", null);
        updateForm("start_season", null);
      }
    } else {
      excludedContent = excludedContent.filter((item) => item.season_guid !== season.season_guid);
    }

    updateForm("excluded", excludedContent);
  }

  return (
    <React.Fragment>
      <form>
        <div className="form-field">
          <label>Item Order</label>
          <Select
            name="order"
            options={orderTypeOptions}
            value={orderTypeOptions.find((option) => option.value === form.order_type)}
            onChange={(input) => updateForm("order_type", input.value)}
            isSearchable={false}
            isOptionDisabled={(option) => option.value === "shuffle"}
          />
        </div>

        <div className="form-field">
          <label>Repeat Rule</label>
          <Select
            name="repeat"
            options={repeatTypeOptions}
            value={repeatTypeOptions.find((option) => option.value === form.repeat_type)}
            onChange={(input) => updateForm("repeat_type", input.value)}
            isSearchable={false}
          />
        </div>

        <div className="form-field">
          <label>Included Content</label>
          <div className="form-field form-field--rows repeat-scheduling-form__checkboxes">
            {!seasons?.length ? ( // useApiRequest  isLoading instead probably
              <Loader />
            ) : (
              seasons.map((season, index) => (
                <div className="repeat-scheduling-form__checkbox" key={index}>
                  <input
                    className="form-checkbox"
                    type="checkbox"
                    name={`season_${season.season_number}`}
                    id={`season_${season.season_number}`}
                    onChange={(e) => updateExcludedContent(e.target.checked, season)}
                    checked={!form.excluded.map((season) => season.season_guid).includes(season.season_guid)}
                  />
                  <label htmlFor={`season_${season.season_number}`}>Season {season.season_number}</label>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="form-field">
          <label>Starting Point</label>

          <Select
            name="start_point"
            options={startingOptions}
            value={startingOptions.find((item) => item.value === form.start_point)}
            onChange={(input) => updateForm("start_point", input.value)}
            isSearchable={false}
          />

          {form.start_point === "select" && seasons.length ? (
            <React.Fragment>
              <div className="repeat-scheduling-form__sub-label">Season</div>
              <div className="repeat-scheduling-form__circle-picker">
                {seasons
                  .filter((season) => !form.excluded.map((season) => season.season_guid).includes(season.season_guid))
                  .map((season, index) => (
                    <div
                      className={`repeat-scheduling-form__circle-picker__item ${form.start_season && form.start_season.season_guid === season.season_guid ? "repeat-scheduling-form__circle-picker__item--selected" : ""}`}
                      onClick={() => {
                        if (!(form.start_season && form.start_season.season_guid === season.season_guid)) {
                          updateForm("start_episode", null);
                          updateForm("start_season", {
                            season_guid: season.season_guid,
                            season_id: season.season_id,
                            season_number: season.season_number,
                          });
                        } else {
                          updateForm("start_season", null);
                        }
                      }}
                      key={`pick_season-${index}`}
                    >
                      {season.season_number}
                    </div>
                  ))}
              </div>

              {form.start_season ? (
                <React.Fragment>
                  <div className="repeat-scheduling-form__sub-label">Episode</div>
                  <div className="repeat-scheduling-form__circle-picker">
                    {seasons
                      .filter(
                        (season) => !form.excluded.map((season) => season.season_guid).includes(season.season_guid),
                      )
                      .find((season) => season.season_guid === form.start_season.season_guid)
                      .episodes.map((episode, index) => (
                        <div
                          className={`repeat-scheduling-form__circle-picker__item ${form.start_episode && form.start_episode.episode_guid === episode.episode_guid ? "repeat-scheduling-form__circle-picker__item--selected" : ""}`}
                          onClick={() =>
                            updateForm("start_episode", {
                              episode_guid: episode.episode_guid,
                              episode_id: episode.episode_id,
                            })
                          }
                          key={`pick_season-${index}`}
                        >
                          {episode.episode_number}
                        </div>
                      ))}
                  </div>
                </React.Fragment>
              ) : null}
            </React.Fragment>
          ) : null}
        </div>

        {form.start_point === "select" ? (
          <div className="form-field">
            <label>Title</label>

            {seasons.length && form.start_season && form.start_episode ? (
              <RepeatSchedulingFormMeta
                episode={seasons
                  .find((season) => season.season_guid === form.start_season.season_guid)
                  .episodes.find((episode) => episode.episode_guid === form.start_episode.episode_guid)}
              />
            ) : null}
          </div>
        ) : null}
      </form>
      <div className="slide-pane__footer slide-pane__footer--bottom">
        {isSubmitting ? (
          <Loader width={45} height={45} />
        ) : (
          <React.Fragment>
            <button className="btn btn--inverse-primary" onClick={() => onClose()}>
              Cancel
            </button>
            <button
              className="btn btn--primary"
              onClick={(e) => {
                e.preventDefault();
                submitForm();
              }}
            >
              Save
            </button>
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
}
